// Text <-> number helpers for the v3 form inputs. Inputs keep the text as typed (so "1." or a
// cleared field survive re-renders) and push the parsed number into the store.

/** "$1,250,000" -> 1250000, "25%" -> 25, "" / "abc" / "-" -> null. Negative numbers are rejected. */
export function parseNumberInput(text: string): number | null {
  const cleaned = text.replace(/[$,%\s]/g, '');
  if (cleaned === '') return null;
  if (!/^\d*\.?\d*$/.test(cleaned) || cleaned === '.') return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

/** The text an input shows for a stored number: en-US grouped ("40,000", "0.5"), blank for null. */
export function numberToInput(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '';
  return n.toLocaleString('en-US', { maximumFractionDigits: 20 });
}

/* ------------------------------------------------------------------- display formatting
 * The stored value stays a plain number (or "" / null); only the text an input shows is grouped.
 * `parseNumberInput` already ignores commas, so formatted and unformatted drafts parse the same. */

const SIGNIFICANT = /[\d.]/;

/** Group the integer digits with commas. */
function groupInt(digits: string): string {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/**
 * Normalise typed or pasted text into grouped display text: keeps digits and the first ".", drops
 * everything else ("$", "%", spaces, letters, stray commas), re-inserts commas. Decimals are kept
 * exactly as typed (so "1.", "0.50" survive), and a field that never gets a "." never gets one.
 */
export function formatNumberText(raw: string): string {
  const cleaned = raw.replace(/[^\d.]/g, '');
  const dot = cleaned.indexOf('.');
  if (dot === -1) return groupInt(cleaned);
  return `${groupInt(cleaned.slice(0, dot))}.${cleaned.slice(dot + 1).replace(/\./g, '')}`;
}

/** Bare numeric text only ("50000", "1,250", "12.5"); a hint like "Add when you have one" or "$5" is left alone. */
export function isNumericText(text: string): boolean {
  return /^[\d,]*\.?\d*$/.test(text) && /\d/.test(text);
}

/** Display text for a value or placeholder: grouped when it is numeric text, untouched otherwise. */
export function displayNumberText(text: string): string {
  return isNumericText(text) ? formatNumberText(text) : text;
}

function sigCount(text: string, upTo: number): number {
  let n = 0;
  for (let i = 0; i < upTo && i < text.length; i++) if (SIGNIFICANT.test(text[i])) n++;
  return n;
}

/** Caret index in `formatted` that sits right after the `n`th digit or ".". */
function caretAfterSig(formatted: string, n: number): number {
  if (n <= 0) return 0;
  let seen = 0;
  for (let i = 0; i < formatted.length; i++) {
    if (SIGNIFICANT.test(formatted[i]) && ++seen === n) return i + 1;
  }
  return formatted.length;
}

/**
 * One edit, resolved. `prev` is the text shown before the keystroke, `raw` the input's value after
 * the browser applied it, `caret` the caret in `raw`. Returns the grouped text and where the caret
 * belongs: the same number of digits to its left, however many commas were added or removed.
 * Backspace (or Delete) that only removes a comma instead removes the digit next to it, so the
 * key always visibly does something.
 */
export function editNumberText(
  prev: string,
  raw: string,
  caret: number,
  inputType?: string,
): { text: string; caret: number } {
  let value = raw;
  let at = caret;
  const strip = (s: string) => s.replace(/[^\d.]/g, '');
  if (raw.length === prev.length - 1 && strip(raw) === strip(prev)) {
    if (inputType === 'deleteContentBackward' && at > 0) {
      value = raw.slice(0, at - 1) + raw.slice(at);
      at -= 1;
    } else if (inputType === 'deleteContentForward') {
      value = raw.slice(0, at) + raw.slice(at + 1);
    }
  }
  const text = formatNumberText(value);
  return { text, caret: caretAfterSig(text, sigCount(value, at)) };
}
